import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import ProgressBar from '@ramonak/react-progress-bar';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AiOutlineCar } from 'react-icons/ai';
import { FiEdit, FiSearch } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';
import { MdAdd, MdFolderDelete, MdUpload } from 'react-icons/md';
import { TbTrash } from 'react-icons/tb';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import Swal from 'sweetalert2';
import { AdminTitle } from '../../components/AdminTitle';
import { Loading } from '../../components/Loading';
import { LoadingConsult } from '../../components/LoadingConsult';
import ReactSelect from '../../components/ReactSelect';
import { StatusModal } from '../../components/StatusModal';
import { StyledButton } from '../../components/StyledButton';
import { StyledInput } from '../../components/StyledInput';
import { StatusModalConfirmClick } from '../../components/StyledModalConfirmClick';
import NewDataTable from '../../components/StyledTableContentRaw';
import { StyledUploadFileModal } from '../../components/StyledUploadFileModal';
import { useUser } from '../../context/UserContext';
import {
  IConsultsPackage,
  consultDebtsToUpdateInfo,
  getStatusConsultsPackage,
  postConsultsPackage,
} from '../../hooks/useDebtsUserVehicle';
import {
  IImportingStatus,
  IVehicleInfo,
  deleteMultipleUserVehicle,
  deleteUserVehicle,
  getInProgressUserVehicles,
  importVehicles,
  useVehicleInfo,
} from '../../hooks/useUserVehicles';
import theme from '../../styles/themes/default';
import { checkToFormatLicensingStatus } from '../../utils/checkToFormatLicensingStatus';
import {
  MAX_VEHICLES_SHEET_SIZE,
  readVehiclesSheet,
} from '../../utils/readVehiclesSheet';
import { ufsNamed } from '../../utils/ufsNamed';
import {
  Actions,
  CheckBoxInput,
  ConsultsPackageInProgress,
  FiltersContainer,
  ImportingProgress,
  VehicleContainer,
} from './style';
import { useVehicle } from '../../context/VehiclesContext';
import { useQueryClient } from 'react-query';
import TableTooltip from '../../components/TableTooltip';

const UserVehicles: FC = () => {
  const { user, updateUser } = useUser();
  const [order, setOrder] = useState<'desc' | 'asc'>('desc');
  const [orderBy, setOrderBy] = useState('updated_at');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [text, setText] = useState<string | undefined>(undefined);
  const [textToShow, setTextToShow] = useState('');
  const [uf, setUf] = useState<{ value: string; label: string } | undefined>(
    undefined,
  );
  const queryClient = useQueryClient();

  const [hasDebts, setHasDebts] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const [licensingStatus, setLicensingStatus] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  const [showBulkImportModal, setShowBulkImportModal] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [typeConfirmModal, setTypeConfirmModal] = useState<
    'success' | 'error' | 'warning' | 'info' | 'question'
  >('question');
  const [messageConfirmModal, setMessageConfirmModal] = useState<
    string | ReactNode
  >('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [typeStatusModal, setTypeStatusModal] = useState<'success' | 'error'>(
    'success',
  );
  const [messageStatusModal, setMessageStatusModal] = useState<
    string | ReactNode
  >('');

  const [importingStatus, setImportingStatus] = useState<
    IImportingStatus | undefined
  >();
  const importingStatusRef = useRef(importingStatus);
  importingStatusRef.current = importingStatus;

  const [fast, setFast] = useState(false);

  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const location = useLocation();

  const {
    pagedVehicles: data,
    isLoading,
    error,
    refetch,
    setPagedVehicleParams,
    consultsPackageStatus,
    currentDebtsConsultsBatch,
    consultsCompletedStatus,
    successfulAndFailedDebtConsults,
    batchDebtsConsultsProgress,
    arrayActionsConfirmModal,
    setArrayActionsConfirmModal,
    handlePostConsultsPackageByUserVehicleId,
    handleConsultsPackageStatus,
    consultsPackageModal,
    setConsultsPackageModal,
    setIsChecked,
    isChecked,
    setIsAllChecked,
    isAllChecked,
    loadingConsult,
    setLoadingConsult,
  } = useVehicle();

  useEffect(() => {
    if (!consultsPackageModal) {
      setShowConfirmModal(false);
    } else {
      switch (consultsPackageModal.type) {
        case 'loading':
          if (consultsPackageModal.success) {
            Swal.fire({
              icon: 'success',
              title: 'Pacote de consultas enviado com sucesso!',
              text: `Total de Consultas enviadas: ${consultsPackageModal.message}`,
              confirmButtonColor: theme.colors.orange.primary,
            }).then((onClose) => setConsultsPackageModal(undefined));
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Falha ao enviar pacote de consultas!',
              html: `Houve um erro inesperado ao enviar pacote de consultas.`,
              confirmButtonColor: theme.colors.orange.primary,
            }).then((onClose) => setConsultsPackageModal(undefined));
          }
          break;

        case 'finished':
          if (consultsPackageModal.success) {
            setTypeStatusModal('success');
            setMessageStatusModal(
              <>
                <br />
                <h5>
                  {`Consultas finalizadas! ${
                    consultsPackageModal.message
                      ? 'Pacote: ' + consultsPackageModal.message
                      : ''
                  }`}
                </h5>
                <hr />
              </>,
            );

            setShowStatusModal(true);
          }
          break;

        default:
          setShowConfirmModal(false);
          setConsultsPackageModal(undefined);
          break;
      }
    }
  }, [consultsPackageModal]);

  useEffect(() => {
    setPagedVehicleParams({
      resultsPerPage: itemsPerPage,
      page: page,
      text: text && text?.length > 2 ? text : undefined,
      uf: uf ? uf.value : undefined,
      hasDebts: hasDebts ? hasDebts.value : undefined,
      licensingStatus: licensingStatus ? licensingStatus.value : undefined,
      orderByDirection: order,
      orderBy,
    });
  }, [itemsPerPage, page, text, uf, hasDebts, licensingStatus, order, orderBy]);

  useEffect(() => {
    setPage(1);
  }, [data?.resultsPerPage, data?.total, itemsPerPage]);

  const headers = [
    { key: 'vehicle.placa', label: 'Placa' },
    { key: 'driver.name', label: 'Condutor', className: 'driverName' },
    { key: 'uf', label: 'UF do veículo' },
    { key: 'vehicle.licensingStatus', label: 'Licenciamento' },
    { key: 'fullTotalDebts', label: 'Débitos pendentes' },
    { key: 'updated_at', label: 'Ultima atualização' },
    { label: 'Ações' },
  ];

  const licensingStatusOptions = [
    { value: 'LICENSED', label: 'Em dia' },
    { value: 'NOT_LICENSED', label: 'Atrasado' },
    { value: 'AVALIABLE_FOR_LICENSING', label: 'A pagar' },
    { value: 'UNDEFINED', label: 'Desconhecido' },
  ];

  const hasDebtsOptions = [
    { value: 'yes', label: 'Sim' },
    { value: 'no', label: 'Não' },
  ];

  const handleChangePlateOrDriverValue = (event: any) => {
    const upperCaseValue = event.target.value;
    setTextToShow(upperCaseValue);

    let debounceTimer;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      setText(upperCaseValue.toUpperCase());
    }, 500);
  };

  const handleToEditUserVehicle = (id: number) => {
    searchParams.set('createOrEdit', 'Editar Cadastro');
    searchParams.set('id', String(id));

    navigate('/vehicles/register?' + searchParams.toString());
  };

  const deleteUserVehicleFunction = async (id: number) => {
    setLoading(true);
    const deleteResult = await deleteUserVehicle(id);

    setLoading(false);
    setShowStatusModal(true);
    setTypeStatusModal(deleteResult?.status < 400 ? 'success' : 'error');
    setMessageStatusModal(
      deleteResult?.status < 400
        ? 'Registro deletado com sucesso'
        : 'Não foi possível deletar registro: ' +
            (deleteResult?.data?.message || deleteResult?.message),
    );
    await queryClient.invalidateQueries('VEHICLES');
    await refetch();
  };

  const handleModalToDeleteVehicle = async (info: any) => {
    setTypeConfirmModal('question');
    setMessageConfirmModal(
      <div>
        <p>Deseja realmente excluir este veículo?</p>
        <p>Placa: {info.vehicle.placa}</p>
      </div>,
    );
    setArrayActionsConfirmModal([() => deleteUserVehicleFunction(info.id)]);
    setShowConfirmModal(true);
  };

  const handleHasDebtsToShow = (total: number) => {
    return total <= 0 ? '--' : Number(total);
  };

  const handleToDetailsUserVehicle = async (info: any) => {
    localStorage.setItem('userVehicleInfo', JSON.stringify(info));

    searchParams.set('id', String(info.id));
    searchParams.set('driver', info?.driver?.name || '--');
    searchParams.set('uf', info.uf);
    navigate('/vehicles/details?' + searchParams.toString());
  };

  const showSheetReadError = (text: string) =>
    Swal.fire({
      title: 'Ops!',
      text,
      icon: 'error',
      confirmButtonColor: theme.colors.orange.primary,
    });

  const handleUploadSheet = (file: File) => {
    if (file.size > MAX_VEHICLES_SHEET_SIZE) {
      showSheetReadError('A planilha excede o tamanho máximo de 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      showSheetReadError('Não foi possível ler a planilha, tente novamente.');
    };
    reader.onload = async (event) => {
      let data: any[] | null;
      try {
        data = readVehiclesSheet(event?.target?.result as ArrayBuffer);
      } catch (e) {
        console.error(e);
        showSheetReadError(
          'Não foi possível ler a planilha. Confira se o arquivo é um .xlsx válido.',
        );
        return;
      }

      if (data) {
        setShowBulkImportModal(false);
        setLoading(true);
        try {
          await importVehicles(data);
          Swal.fire({
            title: 'Sucesso!',
            text: 'Planilha enviada para importação com sucesso.',
            icon: 'success',
            confirmButtonColor: theme.colors.orange.primary,
          });
          getImportingStatus();
        } catch (e: any) {
          console.log(e);
          return Swal.fire({
            title: 'Ops!',
            text:
              e?.response?.data?.message ??
              'Erro ao fazer upload da planilha para importação, tente novamente mais tarde',
            icon: 'error',
            confirmButtonColor: theme.colors.orange.primary,
          });
        } finally {
          setLoading(false);
        }
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const completedProgress = useMemo(() => {
    if (importingStatus) {
      return (
        ((importingStatus.failed + importingStatus.success) /
          importingStatus.total) *
        +100
      );
    }
    return 0;
  }, [importingStatus]);

  const getImportingStatus = async () => {
    try {
      const res = await getInProgressUserVehicles();
      if (res.length > 0) {
        setImportingStatus(res[0]);
        setTimeout(getImportingStatus, 3000);
      } else {
        if (importingStatusRef?.current) {
          setImportingStatus({
            ...importingStatusRef.current,
            success: importingStatusRef.current.total,
            failed: 0,
          });
          setTimeout(() => setImportingStatus(undefined), 5000);
        }

        await refetch();
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const refetchNewVehicle = location?.state?.shouldRefetch || false;
    if (refetchNewVehicle) refetch();
  }, [location?.state?.shouldRefetch]);

  const handleToConsultDebts = async (vehicleId: number) => {
    setLoadingConsult(true);
    await consultDebtsToUpdateInfo(vehicleId)
      .then((res) => {
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Consulta efetuada com sucesso.',
          confirmButtonColor: theme.colors.orange.primary,
        }).then(() => {
          updateUser();
          refetch();
          setFast(true);
          setLoadingConsult(false);
          return;
        });
      })
      .catch((e: any) => {
        if (e?.response?.status === 404) {
          const fullInfoData = e?.response?.data;
          if (
            fullInfoData?.message?.includes('Nenhum débito encontrado') ||
            fullInfoData?.message?.includes('Débitos não encontrados') ||
            fullInfoData?.userVehicleUpdated?.warning
          ) {
            Swal.fire({
              icon: 'info',
              title: fullInfoData?.userVehicleUpdated?.warning
                ? 'Veículo com dívida ativa'
                : 'Consulta efetuada com sucesso!',
              html:
                fullInfoData?.userVehicleUpdated?.warning ??
                'Este veículo não possui débitos pendentes.',
              confirmButtonColor: theme.colors.orange.primary,
            }).then(() => {
              updateUser();
              refetch();
              setFast(true);
              setLoadingConsult(false);
              return;
            });
          } else if (fullInfoData?.message?.includes('Boa notícia')) {
            Swal.fire({
              icon: 'info',
              title: 'Consulta efetuada com sucesso!',
              html: fullInfoData?.message,
              confirmButtonColor: theme.colors.orange.primary,
            }).then(() => {
              updateUser();
              refetch();
              setFast(true);
              setLoadingConsult(false);
              return;
            });
          } else if (
            fullInfoData?.message?.includes('Erro consultando débitos')
          ) {
            Swal.fire({
              icon: 'info',
              title: 'Não foi possível consultar débitos',
              text:
                'Tente novamente ou fale com o suporte - Erro: ' +
                fullInfoData?.message,
              confirmButtonColor: theme.colors.orange.primary,
            }).then(() => {
              updateUser();
              setFast(true);
              setLoadingConsult(false);
              return;
            });
          } else {
            Swal.fire({
              icon: 'info',
              title: 'Houve um problema na consulta',
              text: 'Favor tentar novamente mais tarde, ou entrar em contato com o suporte.',
              confirmButtonColor: theme.colors.orange.primary,
            }).then(() => {
              updateUser();
              setFast(true);
              setLoadingConsult(false);
              return;
            });
          }
        } else if (
          e?.response?.status === 403 &&
          e?.response?.data?.message?.includes('Limite de consultas atingido')
        ) {
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            html: e?.response?.data?.message ?? `Limite de consultas atingido!`,
            confirmButtonColor: theme.colors.orange.primary,
          }).then(() => {
            updateUser();
            refetch();
            setFast(true);
            setLoadingConsult(false);
            return;
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Houve um problema na consulta',
            text: 'Favor tentar novamente mais tarde, ou entrar em contato com o suporte.',
            confirmButtonColor: theme.colors.orange.primary,
          }).then(() => {
            updateUser();
            setFast(true);
            setLoadingConsult(false);
            return;
          });
        }
      });
  };

  // const consultsPackageCompletedProgress = useMemo(() => {
  //   if (consultsPackageStatus) {
  //     return (
  //       (((consultsPackageStatus?.failed || 0) +
  //         consultsPackageStatus.success) /
  //         consultsPackageStatus.quantity) *
  //       +100
  //     );
  //   }
  //   return 0;
  // }, [consultsPackageStatus]);

  const handlePostConsultsPackage = async () => {
    const selectedPlates = Object.entries(isChecked)
      .filter(([, value]) => value)
      .map(([key]) => key);

    if (selectedPlates.length === 0) {
      return Swal.fire({
        icon: 'info',
        title: 'Atenção!',
        text: 'Selecione ao menos um veículo para Consultar',
        confirmButtonColor: theme.colors.orange.primary,
      });
    }

    setTypeConfirmModal('question');
    setMessageConfirmModal(
      <div>
        <p>
          Deseja realmente consultar as {selectedPlates.length} Placas
          selecionadas?
        </p>
        {user && Object.keys(user).length > 0 && (
          <p>
            Número de consultas realizadas:{' '}
            {user?.initial_consult_balance - user?.consult_balance}
          </p>
        )}
      </div>,
    );

    handlePostConsultsPackageByUserVehicleId();
    setShowConfirmModal(true);
  };

  const consultModalMessage = useCallback(
    (info: any): ReactNode => {
      if (info?.uf === 'SE') {
        return (
          <div>
            <p>
              Para parcelar os débitos do seu veículo, você será direcionado
              para o site do
              <a
                href="https://www.detran.se.gov.br/portal/?pg=cons_veiculo"
                target="_blank"
                rel="noreferrer"
              >
                {' '}
                Detran SE
              </a>
              , onde poderá preencher os dados solicitados e consultar os
              débitos existentes. Ao final da consulta, basta escolher a Zignet
              para efetuar o parcelamento.
            </p>
          </div>
        );
      }

      return (
        <div>
          <p>Deseja realmente fazer uma nova consulta?</p>
          <p>Placa: {info?.vehicle?.placa}</p>
          {user && Object.keys(user).length > 0 && (
            <p>
              Número de consultas:{' '}
              {user?.initial_consult_balance - user?.consult_balance}
            </p>
          )}
        </div>
      );
    },
    [user?.consult_balance, user?.initial_consult_balance],
  );

  const consultModalAction = useCallback((info: any) => {
    if (info?.uf === 'SE') {
      return [
        () =>
          window.open(
            'https://www.detran.se.gov.br/portal/?pg=cons_veiculo',
            '_blank',
          ),
      ];
    }
    return [() => handleToConsultDebts(info.id)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetAllSelected = () => {
    if (!isAllChecked && data?.results && data?.results?.length > 0) {
      const newIsChecked = data.results.reduce(
        (acc, each) => ({ ...acc, [each.vehicle.placa]: true }),
        {},
      );
      setIsChecked(newIsChecked);
      setIsAllChecked(true);
    }
    if (isAllChecked && data?.results && data?.results?.length > 0) {
      const newIsChecked = data.results.reduce(
        (acc, each) => ({ ...acc, [each.vehicle.placa]: false }),
        {},
      );
      setIsChecked(newIsChecked);
      setIsAllChecked(false);
    }
  };

  const deleteAllUserVehicleSelected = async () => {
    setLoading(true);

    const arrayToDeleteAndSetResult = data?.results?.filter(
      (each) => isChecked[each.vehicle.placa],
    ) as IVehicleInfo[];

    const removeIds: number[] = [];
    arrayToDeleteAndSetResult.map((userVehicle) =>
      removeIds.push(userVehicle.id as number),
    );

    try {
      await deleteMultipleUserVehicle({ removeIds });
      await queryClient.invalidateQueries('VEHICLES');
      setTypeStatusModal('success');
      setMessageStatusModal('Registros deletados com sucesso');
      setShowStatusModal(true);
      await refetch();
    } catch (error) {
      setTypeStatusModal('error');
      setMessageStatusModal('Erro ao deletar registros');
      setShowStatusModal(true);
    } finally {
      setIsAllChecked(false);
      await queryClient.invalidateQueries('VEHICLES');
      setIsChecked({});
      setLoading(false);
    }
  };

  const handleToDeleteAllSelected = async () => {
    const selectedPlates = Object.entries(isChecked)
      .filter(([, value]) => value)
      .map(([key]) => key);

    if (selectedPlates.length === 0) {
      return Swal.fire({
        icon: 'info',
        title: 'Atenção!',
        text: 'Selecione ao menos um veículo para deletar',
        confirmButtonColor: theme.colors.orange.primary,
      });
    }

    setTypeConfirmModal('question');
    setMessageConfirmModal(
      <div>
        <p>Deseja realmente excluir estes veículos?</p>
        <p>Placas: {selectedPlates.join(', ')}</p>
      </div>,
    );

    setArrayActionsConfirmModal([() => deleteAllUserVehicleSelected()]);
    setShowConfirmModal(true);
  };

  useEffect(() => {
    getImportingStatus();
  }, []);

  return (
    <VehicleContainer>
      {loading && <Loading />}
      {loadingConsult && (
        <LoadingConsult
          fast={fast}
          setLoading={setLoading}
          texts={[
            'Requisição iniciada',
            'Estamos buscando sua placa. Por favor, aguarde',
            'Acessando os dados do veículo no Detran',
            'Processamento de débitos em andamento',
            'Sua consulta está sendo finalizada',
          ]}
          time={120}
        />
      )}
      {showConfirmModal && (
        <StatusModalConfirmClick
          type={typeConfirmModal}
          text={messageConfirmModal}
          setModal={setShowConfirmModal}
          callActions={arrayActionsConfirmModal}
          twoButtons={true}
        />
      )}
      {showStatusModal && (
        <StatusModal
          type={typeStatusModal}
          text={messageStatusModal}
          setModal={setShowStatusModal}
          onClose={() => setConsultsPackageModal(undefined)}
        />
      )}
      {showBulkImportModal && (
        <StyledUploadFileModal
          title="Importação em massa"
          setIsOpen={setShowBulkImportModal}
          fileTypes={['xlsx']}
          inputName="bulk-import"
          confirmButtonText="Importar"
          onConfirm={handleUploadSheet}
          isImportVehicles
          templateUrl={process.env.REACT_APP_TEMPLATE_URL}
        />
      )}
      <AdminTitle title="Listagem de Veículos" icon={<AiOutlineCar />} />
      <FiltersContainer>
        <label htmlFor="" className="inputSearch">
          <StyledInput
            placeholder="Buscar por placa ou condutor"
            value={textToShow}
            onChange={handleChangePlateOrDriverValue}
          />
          <span>
            <FiSearch />
          </span>
        </label>
        <ReactSelect
          placeholder="UF do veículo"
          isLoading={false}
          allOptions={ufsNamed.map(({ value, id }) => ({
            value: id,
            label: value,
          }))}
          setSelectedOption={setUf}
          value={uf}
          name="UF"
          isClearable
        />
        <ReactSelect
          placeholder="Situação do licenciamento"
          isLoading={false}
          allOptions={licensingStatusOptions}
          setSelectedOption={setLicensingStatus}
          value={licensingStatus}
          name="licensing"
          isClearable
        />
        <ReactSelect
          placeholder="Débitos pendentes"
          isLoading={false}
          allOptions={hasDebtsOptions}
          setSelectedOption={setHasDebts}
          value={hasDebts}
          name="debts"
          isClearable
        />
        <StyledButton
          text="Novo cadastro"
          backgroundColor="blue"
          icon={<MdAdd style={{ fontSize: 24 }} />}
          iconPosition="left"
          onClick={() => navigate('/vehicles/register')}
        />
        <div className="importVehicles">
          <StyledButton
            text="Importar veículos em massa"
            backgroundColor={theme.colors.blue.light}
            icon={
              !!importingStatus ? (
                <ClipLoader size={24} color="white" />
              ) : (
                <MdUpload style={{ fontSize: 24 }} />
              )
            }
            iconPosition="left"
            onClick={() => setShowBulkImportModal(true)}
            fullWidth
            disabled={!!importingStatus}
          />

          {importingStatus && (
            <ImportingProgress>
              <ProgressBar
                completed={completedProgress}
                bgColor={theme.colors.blue.light}
                isLabelVisible={false}
              />
              <span>
                {importingStatus.failed + importingStatus.success} de{' '}
                {importingStatus.total} importado(s)
              </span>
            </ImportingProgress>
          )}
        </div>
        <div className="importVehicles">
          <StyledButton
            text="Consultar Selecionados"
            textColor={theme.colors.white}
            backgroundColor={theme.colors.orange.primary}
            icon={<HiOutlineRefresh style={{ fontSize: 24 }} />}
            iconPosition="left"
            onClick={handlePostConsultsPackage}
            disabled={!!consultsPackageStatus}
          />
          {consultsPackageStatus && (
            <ConsultsPackageInProgress>
              <ProgressBar
                completed={batchDebtsConsultsProgress}
                bgColor={theme.colors.blue.light}
                isLabelVisible={false}
              />

              <div>
                <span>
                  {consultsCompletedStatus ?? 0} de{' '}
                  {consultsPackageStatus.consultas.length ?? 0} consultado(s)
                </span>

                {/* {successfulAndFailedDebtConsults?.failed > 0 && (
                  <span>
                    {successfulAndFailedDebtConsults.failed} falharam
                  </span>
                )} */}
              </div>
            </ConsultsPackageInProgress>
          )}
        </div>
        <div>
          <StyledButton
            text="Deletar Selecionados"
            textColor={
              isAllChecked || Object.values(isChecked).some((each) => each)
                ? theme.colors.white
                : theme.colors.gray[600]
            }
            backgroundColor={
              isAllChecked || Object.values(isChecked).some((each) => each)
                ? theme.colors.red.primary
                : theme.colors.red.light
            }
            icon={<MdFolderDelete style={{ fontSize: 24 }} />}
            iconPosition="left"
            onClick={handleToDeleteAllSelected}
          />
        </div>
      </FiltersContainer>

      <NewDataTable
        title="Listagem de Veículos"
        heads={headers}
        hasPagination
        data={data?.results}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        page={Number(data?.currentPage)}
        setPage={setPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        nPages={data?.nPages}
        isLoading={isLoading}
        error={error}
        totalItems={data?.total}
        hasCheckbox={true}
        isChecked={isAllChecked}
        setIsChecked={handleSetAllSelected}
      >
        <TableBody>
          {data &&
            data?.results?.map((each, i) => (
              <TableRow key={each?.vehicle?.placa}>
                <td>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      textAlign: 'start',
                      margin: '0 0 0 12px',
                      maxWidth: 'fit-content',
                    }}
                  >
                    <CheckBoxInput
                      type="checkbox"
                      checked={isChecked[each.vehicle.placa]}
                      onChange={() =>
                        setIsChecked((prev: any) => ({
                          ...prev,
                          [String(each.vehicle.placa)]:
                            !isChecked[each.vehicle.placa],
                        }))
                      }
                    />
                    {each?.vehicle?.placa || '--'}
                  </div>
                </td>
                <td className="driverName">
                  {each?.driver?.name ? (
                    <TableTooltip
                      text={each.driver?.name}
                      tooltipText={each.driver?.name}
                      className=""
                    />
                  ) : (
                    '--'
                  )}
                </td>
                <td>{ufsNamed.find(({ id }) => id === each.uf)?.value}</td>
                <td
                  style={{
                    color: checkToFormatLicensingStatus(each?.vehicle)?.color,
                    fontWeight: 'bold',
                  }}
                >
                  {checkToFormatLicensingStatus(each?.vehicle)?.label}
                </td>
                <td>
                  {handleHasDebtsToShow(
                    each?.fullTotalDebts || 0,
                  ).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>

                <td>
                  {each?.updated_at
                    ? new Date(each?.updated_at)?.toLocaleDateString()
                    : '--'}
                </td>

                <td style={{ textAlign: 'center' }}>
                  <Actions>
                    <div onClick={() => handleToDetailsUserVehicle(each)}>
                      <FiSearch />
                    </div>
                    <div onClick={() => handleToEditUserVehicle(each.id)}>
                      <FiEdit />
                    </div>
                    <div onClick={() => handleModalToDeleteVehicle(each)}>
                      <TbTrash />
                    </div>
                  </Actions>
                </td>
              </TableRow>
            ))}
        </TableBody>
      </NewDataTable>
    </VehicleContainer>
  );
};

export default UserVehicles;
